import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { useNavigate } from "react-router-dom";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  Building2,
  MapPin,
  Calendar,
  UserPlus,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Filter,
  X,
  TriangleAlert,
} from "lucide-react";
import { mockCountries } from "../../data/mockData";
import {
  Country,
  DashboardApiResponse,
  getCountryCity,
  TourRegistrationDashboard,
  TourRegistrationListing,
} from "../../services/newRegistrationService";
import { FullScreenLoading } from "../../components/ui/loader";
import { TORegistration } from "../../types/tourOperator";
import Pagination from "../../ui/pagination";
import { toast } from "sonner";
import useDebounce from "../../hooks/useDebounce";
import { useScreenPermission } from "hooks/useScreenPermission";
import { usePageHeader } from "contexts/PageHeaderContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";

interface TORegistrationAdminPageProps {
  onNavigate?: (page: string) => void;
}
type TabStatus = "pending" | "approved" | "rejected" | "Submitted" | "";
type RegistrationTabStatus = "Submitted" | "approved" | "rejected";
type RegistrationTabCounts = Record<RegistrationTabStatus, number>;

const EMPTY_TAB_COUNTS: RegistrationTabCounts = {
  Submitted: 0,
  approved: 0,
  rejected: 0,
};

export const TORegistrationAdminPage = ({
  onNavigate,
}: TORegistrationAdminPageProps) => {
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const [activeTab, setActiveTab] = useState<TabStatus>("Submitted");
  const [selectedRegistration] = useState<TORegistration | null>(null);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Filter state
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [RegistrationList, setRegistrationList] = useState<TORegistration[]>(
    [],
  );

  // ADVANCE FILTERS STATS
  const [searchTerm, setSearchTerm] = useState("");
  const [filterFields, setFilterFields] = useState({
    itsNumber: "",
    name: "",
    officeName: "",
    nationality: "",
    country: " ",
    status: "Submitted",
    itsNo: "",
    pageNo: 1,
    pageSize: 20,
  });

  const mergedFilters = useMemo(
    () => ({
      ...filterFields,
      searchTerm,
    }),
    [filterFields, searchTerm],
  );

  const debouncedFilters = useDebounce(mergedFilters, 400);

  // Check if current user is super admin
  // const currentUserEmail = localStorage.getItem('currentUserEmail') || 'admin@fhims.com';
  // const isSuperAdmin = currentUserEmail === 'superadmin@fhims.com';

  // Lists
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] =
    useState<DashboardApiResponse | null>(null);
  const [countriesList, setCountriesList] = useState<Country[]>([]);
  const [totalRequestListCount, setTotalRequestListCount] = useState(0);
  const [tabCounts, setTabCounts] =
    useState<RegistrationTabCounts>(EMPTY_TAB_COUNTS);
  const latestListRequest = useRef(0);
  const { icon: ScreenIcon } = useScreenPermission();

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "bg-green-100 text-green-700 border-green-300";
      case "pending":
        return "bg-amber-100 text-amber-700 border-amber-300";
      case "Submitted":
        return "bg-amber-100 text-amber-700 border-amber-300";
      case "rejected":
        return "bg-red-100 text-red-700 border-red-300";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  // Sorting handler
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedListApplicants = useMemo(() => {
    if (!sortColumn) return RegistrationList;

    return [...RegistrationList].sort((a, b) => {
      const getSortValue = (registration: TORegistration) => {
        if (sortColumn === "location") {
          return (
            countriesList.find(
              (country) =>
                String(country.countryId) === String(registration.countryId),
            )?.countryName ?? ""
          );
        }

        return registration[sortColumn as keyof TORegistration] ?? "";
      };

      const aValue = String(getSortValue(a)).toLowerCase();
      const bValue = String(getSortValue(b)).toLowerCase();

      const result = aValue.localeCompare(bValue, undefined, {
        numeric: true,
        sensitivity: "base",
      });

      return sortDirection === "asc" ? result : -result;
    });
  }, [RegistrationList, countriesList, sortColumn, sortDirection]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const clearFilters = () => {
    setFilterFields((prev) => ({
      ...prev,
      itsNumber: "",
      name: "",
      officeName: "",
      country: " ",
      status: " ",
      pageNo: 1, // usually reset pagination
    }));
    setSearchTerm("");
    setActiveTab("Submitted");
  };

  //  FETCH COUNTRY & CITY & DOCUMENT
  const fetchCountryCity = useCallback(async () => {
    try {
      const result = await getCountryCity();
      if (!result) {
        return;
      }
      setCountriesList(result);
    } catch (error) {
      console.error("Failed to load documents");
    }
  }, []);

  useEffect(() => {
    setPageHeader({
      title: "Registration Requests",
      icon: <ScreenIcon className="h-4 w-4" />,
      onBack: () => navigate("/registration"),
    });

    return () => setPageHeader(null);
  }, [navigate, ScreenIcon, setPageHeader]);

  useEffect(() => {
    fetchCountryCity();
  }, [fetchCountryCity]);

  // FETCH DOCUMENT LIST START
  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      const result = await TourRegistrationDashboard();
      if (result) {
        setDashboardData(result);
      }
      //  else {
      //   setApiErrors(result);
      // }
      setLoading(false);
    };
    fetchDashboard();
  }, [selectedRegistration]);

  const fetchVisaList = useCallback(async () => {
    const requestId = ++latestListRequest.current;

    try {
      setLoading(true);
      const normalizedSearchTerm = debouncedFilters.searchTerm.trim();
      const selectedStatus: RegistrationTabStatus =
        debouncedFilters.status === "approved" ||
        debouncedFilters.status === "rejected"
          ? debouncedFilters.status
          : "Submitted";

      const getFiltersForStatus = (status: RegistrationTabStatus) => ({
        ...debouncedFilters,
        searchTerm: normalizedSearchTerm || undefined,
        status,
        pageNo: status === selectedStatus ? debouncedFilters.pageNo : 1,
        pageSize: status === selectedStatus ? debouncedFilters.pageSize : 1,
      });

      const [submittedResult, approvedResult, rejectedResult] =
        await Promise.all([
          TourRegistrationListing(getFiltersForStatus("Submitted")),
          TourRegistrationListing(getFiltersForStatus("approved")),
          TourRegistrationListing(getFiltersForStatus("rejected")),
        ]);

      if (requestId !== latestListRequest.current) {
        return;
      }

      const resultsByStatus = {
        Submitted: submittedResult,
        approved: approvedResult,
        rejected: rejectedResult,
      };
      const selectedResult = resultsByStatus[selectedStatus];

      setRegistrationList(selectedResult?.items ?? []);
      setTotalRequestListCount(selectedResult?.totalCount ?? 0);
      setTabCounts({
        Submitted: submittedResult?.totalCount ?? 0,
        approved: approvedResult?.totalCount ?? 0,
        rejected: rejectedResult?.totalCount ?? 0,
      });
    } catch {
      if (requestId !== latestListRequest.current) {
        return;
      }

      console.log("failed to fetch registration listing");
      toast.error("Failed to fetch registration applications");
    } finally {
      if (requestId === latestListRequest.current) {
        setLoading(false);
      }
    }
  }, [debouncedFilters]);

  useEffect(() => {
    fetchVisaList();
  }, [fetchVisaList, selectedRegistration]);
  // FETCH DOCUMENT LIST END

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card
            className="border-amber-200 bg-amber-50/50"
            style={{ marginTop: "0px" }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600">Pending Approval</p>
                  <p className="text-2xl font-bold text-amber-900">
                    {dashboardData?.submitted || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card
            className="border-green-200 bg-green-50/50"
            style={{ marginTop: "0px" }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Approved</p>
                  <p className="text-2xl font-bold text-green-900">
                    {dashboardData?.approved || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card
            className="border-red-200 bg-red-50/50"
            style={{ marginTop: "0px" }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Rejected</p>
                  <p className="text-2xl font-bold text-red-900">
                    {dashboardData?.rejected || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div
          className="flex flex-col sm:flex-row sm:items-center w-full gap-4"
          style={{ marginTop: "10px" }}
        >
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

              <Input
                placeholder="Search by ITS number, name, or office name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setFilterFields((prev) => ({
                    ...prev,
                    pageNo: 1,
                  }));
                }}
                className="h-10 pl-9 pr-9 shadow-sm"
              />

              {searchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm("");
                    setFilterFields((prev) => ({
                      ...prev,
                      pageNo: 1,
                    }));
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilterDialog(!showFilterDialog)}
              className={`shrink-0 ${
                showFilterDialog
                  ? "text-white"
                  : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
              }`}
              title="Advanced Filters"
            >
              <Filter className="w-4 h-4 sm:mr-0" />
            </Button>
          </div>
        </div>

        {/* List Table */}
        <Card
          className="bg-white border-[#5B9BD5]/30"
          style={{ marginTop: "10px" }}
        >
          <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50">
            <div className="flex flex-col gap-4">
              <div>
                <CardTitle style={{ color: "#5B9BD5" }}>
                  Registration Applications
                </CardTitle>
                {/* <p className="text-sm text-gray-600 mt-1">
                    {filteredList.length} of {currentList.length} application{currentList.length !== 1 ? 's' : ''}
                  </p> */}
              </div>
              <Tabs
                value={activeTab}
                onValueChange={(v) => {
                  if (
                    v === "pending" ||
                    v === "approved" ||
                    v === "rejected" ||
                    v === "Submitted"
                  ) {
                    setActiveTab(v);
                    setFilterFields((prev) => ({
                      ...prev,
                      status: v,
                      pageNo: 1,
                    }));
                  }
                }}
              >
                <TabsList
                  style={{ backgroundColor: "rgba(91, 155, 213, 0.1)" }}
                  className="w-full grid grid-cols-3"
                >
                  <TabsTrigger
                    value="Submitted"
                    className="data-[state=active]:bg-white data-[state=active]:text-gray-900 text-xs sm:text-sm"
                  >
                    <span className="hidden sm:inline">Pending</span>
                    <span className="sm:hidden">Pend</span>
                    <span className="ml-1">({tabCounts.Submitted})</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="approved"
                    className="data-[state=active]:bg-white data-[state=active]:text-gray-900 text-xs sm:text-sm"
                  >
                    <span className="hidden sm:inline">Approved</span>
                    <span className="sm:hidden">Appr</span>
                    <span className="ml-1">({tabCounts.approved})</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="rejected"
                    className="data-[state=active]:bg-white data-[state=active]:text-gray-900 text-xs sm:text-sm"
                  >
                    <span className="hidden sm:inline">Rejected</span>
                    <span className="sm:hidden">Rej</span>
                    <span className="ml-1">({tabCounts.rejected})</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none"
                      onClick={() => handleSort("itsNumber")}
                    >
                      <div className="flex items-center gap-1">
                        ITS No.
                        {sortColumn === "itsNumber" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none"
                      onClick={() => handleSort("firstName")}
                    >
                      <div className="flex items-center gap-1">
                        Name
                        {sortColumn === "firstName" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none"
                      onClick={() => handleSort("officeName")}
                    >
                      <div className="flex items-center gap-1">
                        Office Name
                        {sortColumn === "officeName" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none"
                      onClick={() => handleSort("location")}
                    >
                      <div className="flex items-center gap-1">
                        Location
                        {sortColumn === "location" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none"
                      onClick={() => handleSort("createdAt")}
                    >
                      <div className="flex items-center gap-1">
                        Registration Date
                        {sortColumn === "createdAt" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {sortColumn === "status" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs text-right"
                    >
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {RegistrationList.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center py-8 px-4 text-gray-500 text-sm"
                      >
                        No registrations found
                      </TableCell>
                    </TableRow>
                  ) : (
                    RegistrationList &&
                    sortedListApplicants?.map((registration) => (
                      <TableRow
                        key={registration.id || registration.tourOperatorId}
                        className="hover:bg-sky-50/30"
                      >
                        <TableCell className="px-4 py-3">
                          <span className="text-gray-900 font-mono text-sm">
                            {registration.itsNumber}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-gray-900 text-sm">
                            {registration.firstName} {registration.lastName}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-gray-700 text-sm">
                            {registration.officeName}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-gray-700 text-sm">
                            {
                              countriesList.find(
                                (c) =>
                                  String(c.countryId) ===
                                  String(registration.countryId),
                              )?.countryName
                            }
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-gray-700">
                            {registration.createdAt.split("T")[0]}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <Badge
                            className={`${getStatusColor(registration.status)} text-[10px] px-1.5 py-0.5`}
                          >
                            {registration.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right">
                          {/* <Button
                            size="sm"
                            onClick={() =>
                              onNavigate?.(
                                `/registration/edit/${registration.tourOperatorId}`,
                              )
                            }
                            className="text-white hover:opacity-90 transition-opacity"
                          >
                            <Edit2 className="w-4 h-4 mr-1" />
                          </Button> */}

                          <div className="flex items-center justify-end gap-2">
                            {(registration.pendingPartnerApprovalCount ?? 0) >
                              0 && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="relative inline-flex h-7 w-7 cursor-help items-center justify-center rounded-full bg-amber-100 text-amber-600">
                                      <TriangleAlert className="h-5 w-5" />
                                      <span className="absolute -right-1.5 -top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white ring-2 ring-white">
                                        {
                                          registration.pendingPartnerApprovalCount
                                        }
                                      </span>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="left"
                                    className="w-72 !whitespace-normal break-words text-left leading-relaxed"
                                  >
                                    {registration.pendingPartnerApprovalCount}{" "}
                                    new{" "}
                                    {registration.pendingPartnerApprovalCount ===
                                    1
                                      ? "partner has"
                                      : "partners have"}{" "}
                                    been added after the initial approval and{" "}
                                    {registration.pendingPartnerApprovalCount ===
                                    1
                                      ? "requires"
                                      : "require"}{" "}
                                    review.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <Button
                              size="sm"
                              onClick={() =>
                                onNavigate?.(
                                  `/registration/view/${registration.tourOperatorId}`,
                                )
                              }
                              className="m-1 text-white hover:opacity-90 transition-opacity"
                              style={{ backgroundColor: "#5B9BD5" }}
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              {activeTab === "Submitted" ? "Review" : "View"}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* PAGINATION COMPONENT FOR DRAFT */}
              <Pagination
                totalItems={totalRequestListCount}
                itemsPerPage={filterFields.pageSize}
                currentPage={filterFields.pageNo}
                onPageChange={(page) =>
                  setFilterFields((prev) => ({
                    ...prev,
                    pageNo: page, // convert back to 0-based
                  }))
                }
                onPageSizeChange={(size) =>
                  setFilterFields((prev) => ({
                    ...prev,
                    pageSize: size,
                    pageNo: 1, // IMPORTANT: reset page
                  }))
                }
                label=""
              />
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-200">
              {RegistrationList.length === 0 ? (
                <div className="text-center py-8 px-4 text-gray-500 text-sm">
                  No registrations found
                </div>
              ) : (
                RegistrationList &&
                sortedListApplicants?.map((registration) => (
                  <div
                    key={registration.id || registration.tourOperatorId}
                    className="p-4 hover:bg-sky-50/30"
                  >
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <UserPlus className="w-4 h-4 text-gray-400" />
                            <span className="font-mono text-sm font-medium text-gray-900">
                              {registration.itsNumber}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {(registration.pendingPartnerApprovalCount ?? 0) >
                            0 && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="relative inline-flex h-8 w-8 shrink-0 cursor-help items-center justify-center rounded-full bg-amber-100 text-amber-600">
                                    <TriangleAlert className="h-5 w-5" />
                                    <span className="absolute -right-1.5 -top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white ring-2 ring-white">
                                      {registration.pendingPartnerApprovalCount}
                                    </span>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="left"
                                  className="w-[calc(100vw-5rem)] max-w-72 !whitespace-normal break-words text-left leading-relaxed"
                                >
                                  {registration.pendingPartnerApprovalCount} new{" "}
                                  {registration.pendingPartnerApprovalCount ===
                                  1
                                    ? "partner has"
                                    : "partners have"}{" "}
                                  been added after the initial approval and{" "}
                                  {registration.pendingPartnerApprovalCount ===
                                  1
                                    ? "requires"
                                    : "require"}{" "}
                                  review.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          <Badge
                            className={`${getStatusColor(registration.status)} text-[10px] px-1.5 py-0.5`}
                          >
                            {registration.status}
                          </Badge>
                        </div>
                      </div>

                      <div>
                        <p className="font-medium text-gray-900">
                          {registration.firstName} {registration.lastName}
                        </p>
                      </div>

                      <div className="flex items-start gap-2">
                        <Building2 className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-gray-700">
                          {registration.officeName}
                        </p>
                      </div>

                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-gray-700">
                          {
                            mockCountries.find(
                              (c) => c.id === registration.country,
                            )?.name
                          }
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <p className="text-xs text-gray-600">
                          {registration.registeredDate}
                        </p>
                      </div>

                      <div className="pt-2">
                        <Button
                          size="sm"
                          onClick={() =>
                            onNavigate?.(
                              `/registration/view/${registration.tourOperatorId}`,
                            )
                          }
                          className="w-full text-white hover:opacity-90 transition-opacity"
                          style={{ backgroundColor: "#5B9BD5" }}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          {activeTab === "Submitted" ? "Review" : "View"}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
